import React, { useState, useEffect, Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Menu, Transition } from "@headlessui/react";
import { ReactSVG } from "react-svg";
import Button from "../../common/Button";
import CustomInput from "../../common/CustomInput";
import { ChevronRightIcon, ChevronDownIcon } from "@heroicons/react/24/solid";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { uploadSignature, updateReelMember, deleteSignature } from "../../../redux/modules/user/actions";

// Constants
const ALLOWED_FILE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const VirtualSignature = () => {
  const dispatch = useDispatch();
  const { signatureUploadLoading, currentLoggedInUserDetail } = useSelector((s) => s.users);

  const [signatureFile, setSignatureFile] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState(null);
  const [signatureMarkedForDeletion, setSignatureMarkedForDeletion] = useState(false);
  const [error, setError] = useState("");
  const [showTips, setShowTips] = useState(true);
  const [fullName, setFullName] = useState("");
  const [licenseNumber, setLicenseNumber] = useState("");
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingLicense, setIsEditingLicense] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const restoreSignaturePreview = (signature) => {
    if (signature && typeof signature === "string" && signature.length > 0) {
      setSignaturePreview(
        signature.startsWith("data:") ? signature : `data:image/png;base64,${signature}`
      );
    } else {
      setSignaturePreview(null);
    }
  };

  // Only populate name/license when a saved signature exists
  useEffect(() => {
    if (!currentLoggedInUserDetail) return;
    const { firstName, middleName, lastName, driverLicence, signature } = currentLoggedInUserDetail;

    if (signature && typeof signature === "string" && signature.length > 0) {
      setFullName([firstName, middleName, lastName].filter(Boolean).join(" "));
      setLicenseNumber(driverLicence || "");
      restoreSignaturePreview(signature);
    } else {
      setFullName("");
      setLicenseNumber("");
      setSignaturePreview(null);
    }
  }, [currentLoggedInUserDetail]);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_FILE_TYPES.includes(file.type.toLowerCase())) {
      setError("Invalid file type. Only JPG, PNG, and GIF images are allowed.");
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError("File size exceeds the maximum limit of 5MB.");
      return;
    }

    setError("");
    setSignatureFile(file);
    setSignaturePreview(URL.createObjectURL(file));

    // Auto-populate name and licence from current user on first upload
    if (currentLoggedInUserDetail) {
      const { firstName, middleName, lastName, driverLicence } = currentLoggedInUserDetail;
      setFullName([firstName, middleName, lastName].filter(Boolean).join(" "));
      setLicenseNumber(driverLicence || "");
    }

    setHasChanges(true);
  };

  const handleSave = () => {
    if (!currentLoggedInUserDetail?.userId || !hasChanges) return;

    const nameParts = fullName.trim().split(/\s+/);
    const firstName = nameParts[0] || "";
    const middleName = nameParts.slice(1, -1).join(" ") || "";
    const lastName = nameParts[nameParts.length - 1] || "";

    const updatedUserData = {
      ...currentLoggedInUserDetail,
      firstName,
      middleName,
      lastName,
      driverLicence: licenseNumber,
    };

    if (updatedUserData.companyRoleId !== undefined) {
      updatedUserData.companyRoleId = String(updatedUserData.companyRoleId);
    }
    if (updatedUserData.permissionId !== undefined) {
      updatedUserData.permissionId = String(updatedUserData.permissionId);
    }

    dispatch(updateReelMember(currentLoggedInUserDetail.userId, updatedUserData));

    if (signatureMarkedForDeletion) {
      dispatch(deleteSignature(currentLoggedInUserDetail.userId));
    } else if (signatureFile) {
      dispatch(uploadSignature(currentLoggedInUserDetail.userId, signatureFile));
    }

    setHasChanges(false);
    setIsEditingName(false);
    setIsEditingLicense(false);
    setSignatureFile(null);
    setSignatureMarkedForDeletion(false);

    if (!signatureFile && !signatureMarkedForDeletion && signaturePreview) {
      // Keep existing preview when no new file was selected and not deleting
    } else {
      setSignaturePreview(null);
    }
  };

  const handleCancel = () => {
    setSignatureFile(null);
    setSignatureMarkedForDeletion(false);
    setIsEditingName(false);
    setIsEditingLicense(false);
    setHasChanges(false);
    setError("");

    if (currentLoggedInUserDetail) {
      const { firstName, middleName, lastName, driverLicence, signature } = currentLoggedInUserDetail;
      if (signature && typeof signature === "string" && signature.length > 0) {
        setFullName([firstName, middleName, lastName].filter(Boolean).join(" "));
        setLicenseNumber(driverLicence || "");
        restoreSignaturePreview(signature);
      } else {
        setFullName("");
        setLicenseNumber("");
        setSignaturePreview(null);
      }
    } else {
      setFullName("");
      setLicenseNumber("");
      setSignaturePreview(null);
    }
  };

  const handleEdit = () => {
    setIsEditingName(true);
    setIsEditingLicense(true);
    setHasChanges(true);
  };

  // X button: stage local removal, deferred to Save
  const handleMarkSignatureForDeletion = () => {
    setSignatureFile(null);
    setSignaturePreview(null);
    setSignatureMarkedForDeletion(true);
    setHasChanges(true);
  };

  // Three-dot menu Delete: immediate server call
  const handleDelete = () => {
    if (!currentLoggedInUserDetail?.userId) return;
    dispatch(deleteSignature(currentLoggedInUserDetail.userId));
    setSignatureFile(null);
    setSignaturePreview(null);
    setSignatureMarkedForDeletion(false);
    setFullName("");
    setLicenseNumber("");
    setHasChanges(false);
    setIsEditingName(false);
    setIsEditingLicense(false);
    setError("");
  };

  // Upload disabled when signature exists and not in edit mode
  const isUploadDisabled = !!signaturePreview && !isEditingName;

  return (
    <div className="w-full max-w-[1100px] flex-1 min-h-0 flex flex-col bg-gray-10 border border-gray-100 rounded-[4px] my-4 overflow-hidden">
      {/* Header */}
      <div className="h-[72px] flex-shrink-0 flex justify-between items-center px-6 py-3 bg-gray-20 border-b border-gray-100 rounded-t-[4px]">
        <h1 className="text-[24px] font-bold text-teal-300 leading-[25px]">
          Virtual Signature <span className="font-medium"> - Add your signature to REEL</span>
        </h1>
      </div>

      {/* Scrollable content area */}
      <div className="flex-1 min-h-0 overflow-y-auto px-6 pt-3 pb-0 flex flex-col">
        {/* Description */}
        <div className="flex-shrink-0 text-gray-600 text-[18px] font-semibold">
          When you complete a Clinical Service, REEL will automatically date and time the documentation forms
          <br /> and add your virtual signature.
          <div className="h-[8px] pt-5 border-b border-gray-200"></div>
        </div>

        {/* Tips Section */}
        <div className="flex-shrink-0 bg-white border border-gray-200 rounded-md mt-[30px] overflow-hidden">
          <div className="flex justify-between items-center pt-2 pb-2 px-5">
            <h3 className="text-gray-800 text-[22px] font-medium leading-8">
              Tips to Create Your Signature
            </h3>
            <button
              onClick={() => setShowTips((v) => !v)}
              className="p-2 hover:bg-gray-20 rounded transition-colors flex-shrink-0"
              aria-label="Toggle tips"
            >
              {showTips ? (
                <ChevronDownIcon className="w-[34px] h-[34px] text-gray-600" />
              ) : (
                <ChevronRightIcon className="w-[34px] h-[34px] text-gray-600" />
              )}
            </button>
          </div>
          <div
            className={`grid transition-[grid-template-rows] duration-200 ${
              showTips ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
            }`}
          >
            <div className="overflow-hidden">
              <div className="px-5 pb-3">
                <p className="text-gray-600 font-medium text-base leading-6">
                  You can create this file by signing your name on a piece of paper, then taking a picture of it (ensure
                  the photo doesn't include too much "white space"). When you take the photo, stay close to the signature.
                </p>
                <div className="pt-[10px] pb-[10px] gap-[10px] border-t border-gray-100 mt-2">
                  <p className="text-gray-600 font-medium text-base leading-6">
                    Alternately, you can create an electronic version using{" "}
                    <a
                      href="https://onlinesignature.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-teal-100 hover:text-teal-200 underline"
                    >
                      Online Signature
                    </a>
                    .
                  </p>
                  <ol className="list-decimal ml-4 mt-2 text-gray-600 font-medium text-base leading-6 space-y-1">
                    <li>Click <b>Online Signature</b> and wait until the page fully loads.</li>
                    <li>Select <b>Draw Signature</b> at the top right of the screen.</li>
                    <li>Use your mouse to <b>draw your signature</b> in black space.</li>
                    <li>Make sure the signature is <b>large and clear</b> for best visibility.</li>
                    <li>Click <b>Save</b> once you're done.</li>
                    <li>Select <b>Download Signature</b> to save it to your device.</li>
                    <li>Go into <b>Health Compass Rx</b> and click <b>Upload Signature</b>.</li>
                    <li>
                      If you are not able to click 'Upload Signature', click the <b>three dots</b> in the corner of the
                      box, then choose <b>Edit</b>, and try uploading again.
                    </li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Create Signature Section */}
        <div className="flex-shrink-0 bg-white border border-gray-200 rounded-md mt-[30px] p-5 h-[280px]">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[22px] font-medium text-gray-800">Create your Signature</h3>
            {/* Three-dot menu: only shown when a signature exists */}
            {signaturePreview && (
              <Menu as="div" className="relative self-start">
                <Menu.Button className="rounded-md transition-colors -translate-y-2 p-2">
                  <ReactSVG
                    src="/icons/triple-dot.svg"
                    className="w-1.5 h-1.5"
                    beforeInjection={(svg) => {
                      svg.removeAttribute("width");
                      svg.removeAttribute("height");
                    }}
                  />
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform translate-y-2 opacity-0 scale-95"
                  enterTo="transform translate-y-0 opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform translate-y-0 opacity-100 scale-100"
                  leaveTo="transform translate-y-2 opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 top-full mt-1 w-16 origin-top-right bg-white divide-y divide-gray-20 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-[1000]">
                    <Menu.Item>
                      <button
                        className="block w-full h-8 text-left px-2 py-0 border-b border-b-gray-50 hover:bg-gray-50"
                        onClick={handleEdit}
                      >
                        Edit
                      </button>
                    </Menu.Item>
                    <Menu.Item>
                      <button
                        className="block w-full h-8 text-left px-2 py-0 hover:bg-gray-50"
                        onClick={handleDelete}
                      >
                        Delete
                      </button>
                    </Menu.Item>
                  </Menu.Items>
                </Transition>
              </Menu>
            )}
          </div>

          {/* Upload Area */}
          <div className="flex flex-col border border-gray-200 rounded-[4px] p-[10px] gap-[10px] h-[150px]">
            <div className="w-full flex-1 flex items-center justify-start overflow-hidden relative">
              {signaturePreview && (
                <img src={signaturePreview} alt="" className="h-full w-auto max-w-[50%] object-contain" />
              )}
              {/* Cross icon: only in edit mode AND when a signature is already uploaded */}
              {isEditingName && signaturePreview && (
                <div className="absolute top-1 right-1">
                  <button
                    onClick={handleMarkSignatureForDeletion}
                    className="p-1 rounded-md hover:bg-gray-100 transition-colors"
                    aria-label="Remove signature"
                  >
                    <XMarkIcon className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-[20px]">
              {/* Upload Signature */}
              <label
                className={`flex items-center gap-[10px] h-[46px] min-w-[187px] flex-shrink-0 px-[10px] border border-gray-200 rounded-[4px] bg-white ${
                  !isUploadDisabled ? "cursor-pointer" : ""
                }`}
                onClick={(e) => {
                  if (isUploadDisabled) {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
              >
                <span className="inline-flex items-center justify-center w-6 h-6">
                  <ReactSVG src="/icons/upload-signature-icon.svg" className="w-[18px] h-[18px]" />
                </span>
                <span className="text-gray-600 font-medium text-[16px] leading-6">Upload Signature</span>
                <input
                  type="file"
                  accept="image/png, image/jpeg, image/jpg, image/gif"
                  className="hidden"
                  disabled={signatureUploadLoading || isUploadDisabled}
                  onClick={(e) => {
                    if (isUploadDisabled) {
                      e.preventDefault();
                      return;
                    }
                    e.currentTarget.value = "";
                  }}
                  onChange={(e) => {
                    if (isUploadDisabled) return;
                    handleFileUpload(e);
                    e.currentTarget.value = "";
                  }}
                />
              </label>

              {/* Name Input */}
              <div className="flex items-center h-[46px] flex-1 min-w-[200px] px-3 border border-gray-200 rounded-[4px] bg-white">
                <span className="text-gray-600 font-medium text-[16px] leading-6 mr-2">Name:</span>
                {isEditingName ? (
                  <CustomInput
                    value={fullName}
                    onChange={(e) => {
                      setFullName(e.target.value);
                      setHasChanges(true);
                    }}
                    className="flex-1 border-none text-sm"
                    autoFocus
                  />
                ) : (
                  <div className="flex-1 text-sm text-gray-700 py-2 px-1 cursor-default">
                    {fullName || ""}
                  </div>
                )}
              </div>

              {/* Licence Input */}
              <div className="flex items-center h-[46px] flex-1 min-w-[200px] px-3 border border-gray-200 rounded-[4px] bg-white">
                <span className="text-gray-600 font-medium text-[16px] leading-6 mr-2">Licence:</span>
                {isEditingLicense ? (
                  <CustomInput
                    value={licenseNumber}
                    onChange={(e) => {
                      setLicenseNumber(e.target.value);
                      setHasChanges(true);
                    }}
                    className="flex-1 border-none text-sm"
                  />
                ) : (
                  <div className="flex-1 text-sm text-gray-700 py-2 px-1 cursor-default">
                    {licenseNumber || ""}
                  </div>
                )}
              </div>
            </div>
          </div>

          {error && <p className="text-red-500 text-sm mt-2">{error}</p>}

          <p className="text-gray-600 font-medium text-[16px] leading-6 mt-2">
            Upload an image of your signature that will be printed on documentation forms. Accepted File Types: JPG, GIF,
            or PNG
          </p>
        </div>
      </div>

      {/* Sticky footer — height and font match "Exit My Profile" button */}
      <div className="flex-shrink-0 px-6 pt-[16px] pb-4 flex justify-end gap-4">
        <Button
          type="secondary"
          text="Cancel"
          onClick={handleCancel}
          disabled={signatureUploadLoading}
          className="h-[60px] min-w-[300px] border !border-teal-100 !text-teal-100 hover:!text-REELTeal hover:!border-REELTeal hover:bg-gray-20"
        />
        <Button
          type="primary"
          text={signatureUploadLoading ? "Saving..." : (isEditingName || isEditingLicense) ? "Update & Save" : "Save"}
          onClick={handleSave}
          disabled={!hasChanges || signatureUploadLoading}
          className="h-[60px] min-w-[300px] bg-teal-150 border border-teal-100 hover:!bg-REELTeal disabled:opacity-50"
        />
      </div>
    </div>
  );
};

export default VirtualSignature;
